import React, { Suspense, useCallback, useEffect, useMemo, useRef } from 'react'
import styled, { css } from 'styled-components/macro'
import { GridRowMenu } from 'src/components/Reusable/Grid/GridRowMenu'
import { P } from 'src/components/Reusable/Text/Text.styles'
import { FlexAlign, FlexColumn, FlexRow } from 'src/components/Reusable/Flex'
import { DocumentSourceProvider } from 'src/context/DocumentSource/DocumentSourceContext'
import palette from 'src/designSystem/variables/palette'
import { ReactComponent as EroEastIcon } from 'src/components/Icons/Brand/ero_dark_east.svg'
import { CloseSVG } from 'src/components/Icons/Basic'
import { ArtifactPanelProvider } from 'src/pages/TrusteroIntelligence/chatpanel/chatPanel.context'
import { ChatStateProvider } from 'src/pages/TrusteroIntelligence/trusteroIntelligence.context'
import {
  useGetChat,
  useGetOrCreateAppCopilotChat,
  useResetAppCopilotChat,
} from 'src/pages/TrusteroIntelligence/trusteroIntelligence.hooks'
import { CopilotBot } from '../CopilotBot'
import { useAppCopilotContext } from './AppCopilot.context'

const LazyChatConversationPanel = React.lazy(async () => {
  const module = await import(
    'src/pages/TrusteroIntelligence/conversation/ChatConversationPanel'
  )
  return { default: module.ChatConversationPanel }
})

const AppCopilotContainer = styled.div<{
  $widthPercent: number
  $open: boolean
  $isResizing: boolean
}>`
  position: relative;
  height: 100%;
  flex: 0 0
    ${({ $open, $widthPercent }) => ($open ? `${$widthPercent}%` : '0%')};
  width: ${({ $open, $widthPercent }) => ($open ? `${$widthPercent}%` : '0%')};
  min-width: ${({ $open }) => ($open ? '360px' : '0')};
  max-width: ${({ $open }) => ($open ? 'calc(66% - 12px)' : '0')};
  margin-right: ${({ $open }) => ($open ? '12px' : '0')};
  background: ${palette.neutral.white};
  border-top-left-radius: 20px;
  box-shadow: -8px 0px 20px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  overflow: ${({ $open }) => ($open ? 'visible' : 'hidden')};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: ${({ $isResizing }) =>
    $isResizing
      ? 'none'
      : 'width 0.25s ease, flex-basis 0.25s ease, min-width 0.25s ease, margin-right 0.25s ease'};

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 38px;
    bottom: 0;
    width: 2px;
    background: linear-gradient(180deg, #bb83ff 0%, #4381fd 100%);
    pointer-events: none;
  }
`

const IconStyle = css`
  cursor: pointer;
  path {
    fill: ${palette.neutral.white};
  },
`

const AppCopilotHeader = styled.div`
  min-height: 38px;
  max-height: 38px;
  background: linear-gradient(90deg, #bb83ff 0%, #4381fd 100%);
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top-left-radius: 20px;
  padding: 0 12px;
`

const ActionIconsRow = styled(FlexRow)`
  .dropdown-menu {
    min-width: fit-content !important;
    padding: 0 !important;
  }

  .dropdown-toggle {
    width: 16px;
    svg {
      height: 16px;
      ${IconStyle}
    }
  }

  svg:hover {
    path {
      fill: ${palette.neutral[50]};
    }
  }

  button.dropdown-toggle:hover {
    background-color: transparent !important;
  }
`

const CloseButton = styled(CloseSVG)`
  width: 12px;
  height: 12px;
  ${IconStyle}
`

const HeaderEroIcon = styled(EroEastIcon)`
  width: 16px;
  height: 16px;
`

const CopilotBody = styled(FlexColumn)`
  height: calc(100% - 38px);
  width: 100%;
  align-items: stretch;
  overflow: hidden;
`

const DragHandle = styled.button`
  position: absolute;
  left: -4px;
  top: 50%;
  transform: translateY(-50%);
  width: 10px;
  height: 21px;
  border: none;
  border-radius: 20px;
  background: linear-gradient(180deg, #b583ff 0%, #5381fd 100%);
  box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.25);
  cursor: grab;
  padding: 0;
  z-index: 2;
`

export const AppCopilot = (): JSX.Element => {
  const {
    title,
    show,
    setShow,
    showBotTrigger,
    topContent,
    chatId,
    setChatId,
    referringContext,
    widthPercent,
    setWidthPercent,
    registerSendMessage,
  } = useAppCopilotContext()
  const {
    data: chatResponse,
    isLoading,
    mutate: mutateGetChat,
  } = useGetChat(chatId ?? '')
  const getOrCreateAppCopilotChat = useGetOrCreateAppCopilotChat()
  const resetAppCopilotChat = useResetAppCopilotChat()

  const dragStartXRef = useRef<number>()
  const dragStartWidthRef = useRef<number>()
  const rafRef = useRef<number>()
  const pendingWidthRef = useRef<number>()
  const [isResizing, setIsResizing] = React.useState(false)

  const syncChatForReferringContext = useCallback(async () => {
    const response = await getOrCreateAppCopilotChat(referringContext, title)
    setChatId(response.chat.getId())
  }, [getOrCreateAppCopilotChat, referringContext, title, setChatId])

  useEffect(() => {
    if (!show || chatId) {
      return
    }
    void syncChatForReferringContext()
  }, [show, chatId, syncChatForReferringContext])

  const onClearChat = useCallback(async () => {
    const response = await resetAppCopilotChat(referringContext, title)
    setChatId(response.chat.getId())
  }, [resetAppCopilotChat, referringContext, title, setChatId])

  const menuItems = useMemo(
    () => [
      {
        key: 'clear_chat',
        item: <span onClick={() => void onClearChat()}>Clear chat</span>,
      },
    ],
    [onClearChat],
  )

  const onMouseMove = useCallback(
    (event: MouseEvent) => {
      if (
        dragStartXRef.current === undefined ||
        dragStartWidthRef.current === undefined
      ) {
        return
      }
      const deltaPx = dragStartXRef.current - event.clientX
      const deltaPercent = (deltaPx / window.innerWidth) * 100
      pendingWidthRef.current = dragStartWidthRef.current + deltaPercent
      if (rafRef.current !== undefined) {
        return
      }
      rafRef.current = window.requestAnimationFrame(() => {
        if (pendingWidthRef.current !== undefined) {
          setWidthPercent(pendingWidthRef.current)
        }
        rafRef.current = undefined
      })
    },
    [setWidthPercent],
  )

  const onMouseUp = useCallback(() => {
    dragStartXRef.current = undefined
    dragStartWidthRef.current = undefined
    setIsResizing(false)
    if (rafRef.current !== undefined) {
      window.cancelAnimationFrame(rafRef.current)
      rafRef.current = undefined
    }
    if (pendingWidthRef.current !== undefined) {
      setWidthPercent(pendingWidthRef.current)
      pendingWidthRef.current = undefined
    }
  }, [setWidthPercent])

  const onResizeStart = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
      event.stopPropagation()
      dragStartXRef.current = event.clientX
      dragStartWidthRef.current = widthPercent
      setIsResizing(true)
    },
    [widthPercent],
  )

  useEffect(() => {
    if (!show && isResizing) {
      onMouseUp()
    }
  }, [show, isResizing, onMouseUp])

  useEffect(() => {
    if (!isResizing) {
      return
    }
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }
  }, [isResizing, onMouseMove, onMouseUp])

  useEffect(() => {
    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
      if (rafRef.current !== undefined) {
        window.cancelAnimationFrame(rafRef.current)
        rafRef.current = undefined
      }
    }
  }, [onMouseMove, onMouseUp])

  const chat = useMemo(() => chatResponse?.getChat(), [chatResponse])
  const noopSetIsPrivate = useCallback(() => {
    return
  }, [])

  const onFirstMessage = useCallback(async () => {
    if (!mutateGetChat) {
      return
    }
    await mutateGetChat()
  }, [mutateGetChat])

  return (
    <>
      {showBotTrigger && !show && <CopilotBot onClick={() => setShow(true)} />}
      <AppCopilotContainer
        $widthPercent={widthPercent}
        $open={show}
        $isResizing={isResizing}
      >
        {show && (
          <DragHandle
            aria-label="Resize copilot panel"
            onMouseDown={onResizeStart}
          />
        )}
        <AppCopilotHeader>
          <FlexRow align={FlexAlign.CENTER} gap={4}>
            <HeaderEroIcon />
            <P $fontSize={16} $fontweight={400} $color={palette.neutral.white}>
              {title}
            </P>
          </FlexRow>
          <ActionIconsRow align={FlexAlign.CENTER} gap={4}>
            <GridRowMenu
              id={`app-copilot-header-menu-${chatId || 'new'}`}
              items={menuItems}
            />
            <CloseButton onClick={() => setShow(false)} />
          </ActionIconsRow>
        </AppCopilotHeader>
        <CopilotBody>
          {topContent}
          <Suspense
            fallback={
              <P
                $fontSize={13}
                $color={palette.neutral[400]}
                style={{ padding: 12 }}
              >
                Loading conversation...
              </P>
            }
          >
            <DocumentSourceProvider>
              <ChatStateProvider>
                <ArtifactPanelProvider>
                  <LazyChatConversationPanel
                    chat={chat}
                    isPrivate={false}
                    setIsPrivate={noopSetIsPrivate}
                    onFirstMessage={onFirstMessage}
                    isLoading={!chatId || isLoading}
                    isCopilotMode={true}
                    onRegisterSendMessage={registerSendMessage}
                  />
                </ArtifactPanelProvider>
              </ChatStateProvider>
            </DocumentSourceProvider>
          </Suspense>
        </CopilotBody>
      </AppCopilotContainer>
    </>
  )
}
